import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: number;
  username: string;
  email?: string;
  full_name?: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_admin?: boolean;
  created_at: string;
  last_login?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Match analyticsApi.ts pattern for API base URL
const getApiBaseUrl = (): string => {
  // In production, use relative URLs to go through Vercel proxy
  if (import.meta.env.PROD) {
    return '';  // Use relative URLs in production
  }
  
  // In development, use configured URL or localhost
  const viteUrl = import.meta.env.VITE_ANALYTICS_API_BASE_URL as string | undefined;
  return viteUrl || 'http://localhost:8080';
};

const API_BASE_URL = getApiBaseUrl();
const TOKEN_KEY = 'series_analytics_token';
const TOKEN_EXPIRY_KEY = 'series_analytics_token_expiry';

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Save token to localStorage with expiry (for SSE query param compatibility)
  const saveToken = (accessToken: string, expiresIn: number) => {
    try {
      localStorage.setItem(TOKEN_KEY, accessToken);
      const expiryTime = Date.now() + (expiresIn * 1000);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
      setToken(accessToken);
    } catch {}
  };

  // Clear token from localStorage
  const clearToken = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    setToken(null);
  };

  // Check if token is still valid
  const isTokenValid = (): boolean => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    
    if (!storedToken || !expiryTime) {
      return false;
    }

    const now = Date.now();
    const expiry = parseInt(expiryTime, 10);
    
    if (now >= expiry) {
      clearToken();
      return false;
    }

    return true;
  };

  // Login function
  const login = async (username: string, password: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      saveToken(data.access_token, data.expires_in);

      // Cookie set by server; fetch user info (credentials included)
      await checkAuth();
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    clearToken();
    setUser(null);
    
    // Optional: Call logout endpoint
    fetch(`${API_BASE_URL}/api/v1/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    }).catch(() => {
      // Ignore errors on logout
    });
  };

  // Check authentication status
  const checkAuth = async (accessToken?: string) => {
    const authToken = accessToken || token || localStorage.getItem(TOKEN_KEY);

    try {
      const url = authToken
        ? `${API_BASE_URL}/api/v1/auth/me?token=${encodeURIComponent(authToken)}`
        : `${API_BASE_URL}/api/v1/auth/me`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : undefined,
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        if (!token && authToken) {
          setToken(authToken);
        }
      } else {
        clearToken();
        setUser(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      clearToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (storedToken && isTokenValid()) {
      setToken(storedToken);
      checkAuth(storedToken);
    } else {
      // Even without stored token, server cookie may be present; try auth
      checkAuth(undefined);
    }
  }, []);

  // Add token to API service headers
  useEffect(() => {
    if (token) {
      // Update default headers for API service if it exists
      const setAuthHeader = async () => {
        try {
          const AnalyticsApiService = (await import('../services/analyticsApi')).default;
          if (AnalyticsApiService.setAuthToken) {
            AnalyticsApiService.setAuthToken(token);
          }
        } catch (error) {
          console.error('Failed to set auth token on API service:', error);
        }
      };
      setAuthHeader();
    }
  }, [token]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
